import {
    clearBrowseSeoMeta,
    setBrowseBaseCategoryIds,
    setBrowseSeoMeta,
} from '@/redux/modules/browsePath/browsePath.reducer';
import { getCategoryEntries } from '@/redux/modules/category/category.selectors';
import { getCatalogIdsForCategory } from '@/redux/modules/categoryCatalogCards';
import { fetchCategoryRelatedSearches } from '@/redux/modules/categoryRelatedSearches';
import { fetchCombinationCategory } from '@/redux/modules/combinationCategory';
import { getDeployment } from '@/redux/modules/config';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { getItemSummarySellerId, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchItemFacetsIfNeeded, getItemFacets } from '@/redux/modules/itemFacets';
import { searchCategory } from '@/redux/modules/landingPage';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { getSearchedItemIds, getSearchUiState } from '@/redux/modules/search/search.selectors';
import { DEFAULT_SORT_ORDER } from '@/redux/modules/search/search.types';
import { getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { fetchSimilarItemsIfNeeded, getSimilarItemIds } from '@/redux/modules/similarItems';
import { fetchSubcategories } from '@/redux/modules/subcategoryTiles';
import { fetchSuggestedCategories } from '@/redux/modules/suggestedCategoryTiles';
import { FilterAction } from '@/types/SearchFacet';
import { mapBrowsePairsToSearchOptionIds, resolveTopCategoryIdFromFacets } from '@/utils/browsePath/browsePath';
import { getFacetIds } from '@/utils/ItemPageUtils';
import { getPreservedRedirectParams } from '@/utils/queryParams';
import { getCategoryIdsFromPathname, getCategoryNameFromPathname, urlSafeTitle } from '@/utils/urls';

import { runPreloadPhase, runSearchPhase, runSecondaryPhase } from './categorySearchData/asyncPhases';
import { resolveBrowseInputs } from './categorySearchData/browseResolution';
import {
    buildCategorySearchQuery,
    CATEGORY_ID_BACKED_BROWSE_FACET_TYPES,
    DEFAULT_PRICE_GUIDE_SORT,
    mergeCategoryIdsIntoQueryParams,
} from './categorySearchData/queryBuilders';
import {
    getCanonicalBrowseRedirectUrl,
    getEarlyBrowseRedirectUrl,
    getLegacyQuadBrowseRedirectUrl,
} from './categorySearchData/redirects';
import { buildRouteContext } from './categorySearchData/routeContext';
import { CategoryEntryLite, CategoryLoaderParams } from './categorySearchData/types';

const getLandingPageIds = (routeContext: ReturnType<typeof buildRouteContext>, baseCategoryIds: number[]) => {
    if (routeContext.legacyPathParsed) {
        return [Number(routeContext.legacyPathParsed.numericCategoryId)];
    }
    if (routeContext.isBrowseSlugRoute) {
        return baseCategoryIds;
    }
    if (routeContext.isCombinationCategory) {
        return [routeContext.legacyCategoryId, routeContext.legacyCategoryId2];
    }
    if (routeContext.hasRegularLegacyCategory) {
        return [routeContext.legacyCategoryId];
    }
    return [];
};

export default ({ location, match, redirect, routerState, store }: CategoryLoaderParams) => {
    const { params } = match;
    const { dispatch: rawDispatch, getState } = store;
    const dispatch = (action: any) => (rawDispatch as any)(action);

    const routeContext = buildRouteContext({
        location: { pathname: location.pathname, search: location.search },
        params,
    });

    const earlyBrowseRedirectUrl = getEarlyBrowseRedirectUrl({
        browseParsed: routeContext.browseParsed,
        pathname: routeContext.pathname,
        queryParams: routeContext.queryParams,
    });
    if (earlyBrowseRedirectUrl) {
        return redirect(earlyBrowseRedirectUrl, true);
    }

    const canonicalBrowseRedirectUrl = getCanonicalBrowseRedirectUrl({
        locationSearch: routeContext.locationSearch,
        pathname: routeContext.pathname,
    });
    if (canonicalBrowseRedirectUrl) {
        return redirect(canonicalBrowseRedirectUrl, true);
    }

    const legacyQuadBrowseRedirectUrl = getLegacyQuadBrowseRedirectUrl({
        locationSearch: routeContext.locationSearch,
        pathname: routeContext.pathname,
    });
    if (legacyQuadBrowseRedirectUrl) {
        return redirect(legacyQuadBrowseRedirectUrl, true);
    }

    return runPreloadPhase({
        dispatch,
        isCombinationCategory: routeContext.isCombinationCategory,
        itemId: routeContext.itemId,
        legacyCategoryId2: routeContext.legacyCategoryId2,
        params,
        pathname: routeContext.pathname,
        queryParams: routeContext.queryParams,
    }).then(async ({ combinationIds }) => {
        dispatch(clearBrowseSeoMeta());

        const stateAfterFacets = getState();
        const deployment = String(getDeployment(stateAfterFacets) || '');

        const browseResolution = await resolveBrowseInputs({
            deployment,
            locationSearch: routeContext.locationSearch,
            routeContext,
            searchFacets: getSearchFacets(stateAfterFacets),
            stateAfterFacets,
        });
        if (browseResolution.redirectUrl) {
            return redirect(browseResolution.redirectUrl, true);
        }

        const {
            baseCategoryIds,
            browseFacetOptionIds,
            browsePathPairsForPostSearchRemap,
            keywordFallbackForUnresolvedBrowse,
            shouldAttemptPostSearchBrowseRemap,
            unresolvedBrowseSlugForKeywordSearch,
        } = browseResolution;

        const browseSlugForState =
            routeContext.isBrowseSlugRoute && params.browseCategorySlug ? params.browseCategorySlug : null;
        dispatch(
            setBrowseBaseCategoryIds({
                ids: baseCategoryIds,
                browseCategorySlug: browseSlugForState,
                browsePathname: routeContext.pathname,
            })
        );
        const isBrowseRoute = !routeContext.archivedSearch && routeContext.pageName === 'category';
        if (isBrowseRoute && browseResolution.seo) {
            dispatch(setBrowseSeoMeta(browseResolution.seo));
        } else {
            dispatch(clearBrowseSeoMeta());
        }

        const finalCategoryIdForTiles = baseCategoryIds.length ? baseCategoryIds[baseCategoryIds.length - 1] : 0;
        const adsCategoryKey = baseCategoryIds.length
            ? baseCategoryIds.join(',')
            : getCategoryIdsFromPathname(routeContext.pathname);
        /*
         * The route context builds `relatedSearchTilesParams` before browse resolution runs, so for
         * browse-slug routes whose top-cat slug is not in the hardcoded alias map (e.g. `/c/chairs/`),
         * `categoryIds` comes out empty. Patch with the browse-resolved ids so the tiles fetch always
         * has a valid category id.
         */
        const relatedSearchTilesParams =
            routeContext.relatedSearchTilesParams.categoryIds.length === 0 && finalCategoryIdForTiles > 0
                ? { ...routeContext.relatedSearchTilesParams, categoryIds: [finalCategoryIdForTiles] }
                : routeContext.relatedSearchTilesParams;
        await runSecondaryPhase({
            adsCategoryKey,
            dispatch,
            finalCategoryIdForTiles,
            itemId: routeContext.itemId,
            keywords: routeContext.keywords,
            pageName: routeContext.pageName,
            pathname: routeContext.pathname,
            relatedSearchTilesParams,
            searchTerm:
                params.browseCategorySlug?.replace(/-/g, ' ') || getCategoryNameFromPathname(routeContext.pathname),
            shouldFetchCategoryBanner: baseCategoryIds.length > 0,
        });

        const currentState = getState();
        const defaultSortOrder = routeContext.archivedSearch ? DEFAULT_PRICE_GUIDE_SORT : DEFAULT_SORT_ORDER;
        const pageSize = getSearchPageSize(currentState);
        const catalogIds = getCatalogIdsForCategory(currentState);
        const rawFacets = getSearchFacets(currentState);
        /*
         * Inject a fallback keyword whenever the page would otherwise render unfiltered or empty.
         * Two derived sources combine into one keyword so the initial `/search/v4/web` call returns
         * relevance-narrowed items + aggregations the remap step can promote to exact facet ids:
         *   1. `slugKeyword`: the URL's top-cat slug, used when `baseCategoryIds` is empty (unknown
         *      L1 not in the hardcoded alias map AND the resolver returned no ids — e.g. `watches`).
         *      Without this, results would span unrelated categories with no L1 context.
         *   2. `pairKeyword`: the URL's dimension/value slugs joined, used whenever browse pairs are
         *      unresolved (e.g. `creator/patek-philippe` → "patek philippe"). Drives both the
         *      relevance ranking AND the aggregations needed to recover the exact facet id.
         * When both apply, combine them so the search has full URL context (e.g. `/c/watches/creator/
         * patek-philippe/` → "watches patek philippe"). Skipped when the request already carries a
         * `keyword` query param so we don't clobber the user's intent.
         */
        const slugKeyword = unresolvedBrowseSlugForKeywordSearch
            ? unresolvedBrowseSlugForKeywordSearch.replace(/-/g, ' ').trim() || null
            : null;
        const pairKeyword = keywordFallbackForUnresolvedBrowse;
        const fallbackKeyword = pairKeyword
            ? slugKeyword
                ? `${slugKeyword} ${pairKeyword}`
                : pairKeyword
            : slugKeyword;
        const queryParamsForSearchInputs =
            fallbackKeyword && !routeContext.queryParams.keyword
                ? { ...routeContext.queryParams, keyword: fallbackKeyword }
                : routeContext.queryParams;
        const { queryParamsForSearch, searchQuery } = buildCategorySearchQuery({
            browseFacetOptionIds,
            defaultSortOrder,
            pathname: routeContext.pathname,
            queryParams: queryParamsForSearchInputs,
            rawFacets,
            searchPageSize: pageSize,
        });

        const landingPageIds = getLandingPageIds(routeContext, baseCategoryIds);
        const nonFilterActions: FilterAction[] = [
            FilterAction.PageChanged,
            FilterAction.PageSizeChanged,
            FilterAction.SortByChanged,
            FilterAction.ViewPriceResults,
            FilterAction.ViewUpcomingItems,
        ];
        const isFilterRefinement =
            Boolean(routerState?.filterAction) && !nonFilterActions.includes(routerState?.filterAction);
        /* Initial server render needs facet aggregations to render filters. SSR layer omits `window`. */
        const isSSR = typeof window === 'undefined';
        const needsAggs = isSSR || isFilterRefinement || shouldAttemptPostSearchBrowseRemap;

        /*
         * Route the initial search through `submitSearch` (search-party's `/search/v4/web`) instead of
         * `searchCategory` (`/search/v4/categoryweb`) whenever we have a fallback keyword. `categoryweb`
         * ignores `searchTerm` for category browses (it's a category-only endpoint), so the keyword
         * narrowing wouldn't actually narrow the result set or the facet aggregations — defeats the
         * point of the fallback. `/web` honors `categories` + `searchTerm` together, returning the
         * keyword-narrowed items within the top-cat.
         *
         * Re-dispatch (when the remap below finds an exact facet id) goes back through `searchCategory`
         * since we then have the proper id filter and don't need the keyword.
         */
        const useKeywordSearchFallback = Boolean(fallbackKeyword);

        await runSearchPhase({
            catalogIds,
            dispatch,
            landingPageIds,
            needsAggs,
            searchQuery,
            useKeywordSearchFallback,
        });

        let searchState = getState();
        if (shouldAttemptPostSearchBrowseRemap && browsePathPairsForPostSearchRemap.length > 0) {
            const postSearchFacets = getSearchFacets(searchState);
            const remappedBrowseFacetOptionIds = mapBrowsePairsToSearchOptionIds(
                browsePathPairsForPostSearchRemap,
                postSearchFacets
            );
            const remappedHasAnyFacetIds = Object.values(remappedBrowseFacetOptionIds).some(
                (ids) => Array.isArray(ids) && ids.length > 0
            );

            /*
             * When `landingPageIds` is empty (unknown L1 — resolver returned nothing and the slug
             * isn't in the hardcoded alias map), backfill it from the post-search F1 (category)
             * facet by matching the URL's top-cat slug against the option names. This is the only
             * way to recover the L1 id for these URLs without a backend deploy. Persist the
             * recovered id to Redux so downstream selectors (filter drawer, ads, category banner)
             * see the right category context.
             */
            const recoveredTopCategoryId =
                landingPageIds.length === 0 && remappedHasAnyFacetIds
                    ? resolveTopCategoryIdFromFacets(routeContext.params.browseCategorySlug, postSearchFacets)
                    : null;
            const effectiveLandingPageIds = recoveredTopCategoryId !== null ? [recoveredTopCategoryId] : landingPageIds;
            /*
             * Category-scoped widgets (subcategory tiles, suggested categories, related searches)
             * fired in `runSecondaryPhase` with `finalCategoryIdForTiles=0` because we hadn't
             * recovered the L1 id yet — they short-circuited or pulled the wrong slice. Re-fire
             * with the recovered id so the "Dive deeper into subcategories" rail and related
             * widgets render the right content. MUST be awaited so SSR serializes the populated
             * state into the hydrated HTML; without the await the client gets an empty rail.
             */
            const categoryWidgetRefetchPromises: Promise<any>[] = [];
            if (recoveredTopCategoryId !== null) {
                dispatch(
                    setBrowseBaseCategoryIds({
                        ids: effectiveLandingPageIds,
                        browseCategorySlug: browseSlugForState,
                        browsePathname: routeContext.pathname,
                    })
                );
                const recoveredIdString = String(recoveredTopCategoryId);
                categoryWidgetRefetchPromises.push(
                    dispatch(fetchSubcategories(recoveredIdString)),
                    dispatch(fetchSuggestedCategories(recoveredIdString)),
                    dispatch(fetchCategoryRelatedSearches([routeContext.itemId], recoveredIdString))
                );
            }

            if (remappedHasAnyFacetIds && effectiveLandingPageIds.length > 0) {
                /* Re-dispatch via categoryweb with the resolved facet ids — exact filter supersedes
                 * the keyword-narrowed initial search. If the initial search injected
                 * `keywordFallbackForUnresolvedBrowse`, drop it now; the user's own `?keyword=…` (if
                 * any) stays intact via `routeContext.queryParams.keyword`. */
                const remappedCategoryIdValues = Object.entries(remappedBrowseFacetOptionIds).flatMap(
                    ([facetType, ids]) => (CATEGORY_ID_BACKED_BROWSE_FACET_TYPES.has(facetType) ? ids : [])
                );
                const rerunBaseQueryParams = fallbackKeyword
                    ? { ...queryParamsForSearch, keyword: routeContext.queryParams.keyword ?? '' }
                    : queryParamsForSearch;
                const rerunQueryParams = mergeCategoryIdsIntoQueryParams(
                    rerunBaseQueryParams,
                    remappedCategoryIdValues
                );
                const { searchQuery: remappedSearchQuery } = buildCategorySearchQuery({
                    browseFacetOptionIds: remappedBrowseFacetOptionIds,
                    defaultSortOrder,
                    pathname: routeContext.pathname,
                    queryParams: rerunQueryParams,
                    rawFacets: postSearchFacets,
                    searchPageSize: pageSize,
                });

                await Promise.all([
                    dispatch(
                        searchCategory({
                            ids: effectiveLandingPageIds,
                            pageName: null,
                            searchQuery: remappedSearchQuery,
                            skipAggs: !needsAggs,
                        })
                    ),
                    ...categoryWidgetRefetchPromises,
                ]);
                searchState = getState();
            } else if (categoryWidgetRefetchPromises.length > 0) {
                /* No re-dispatch (remap missed) but we still recovered the L1 id — finish the
                 * widget refetches so SSR serializes them. */
                await Promise.all(categoryWidgetRefetchPromises);
            }
            /* If the remap still fails after the keyword-narrowed initial search, leave the
             * keyword-narrowed results in place — they're relevant to the URL slug and a much
             * better degradation than rendering the unfiltered top-cat. The canonical URL stays
             * put; SEO indexing depends on the backend eventually returning facet ids for these
             * paths. */
        }

        const itemIds = getSearchedItemIds(searchState);
        const followUpPromises: Promise<any>[] = [];
        const preservedParams = getPreservedRedirectParams(routeContext.queryParams);

        if (itemIds.length === 0 && routeContext.queryParams?.page > 0) {
            redirect?.(`${location.pathname}${preservedParams}`, true);
            return;
        }

        if (itemIds.length === 0 && combinationIds) {
            const category = (Object.values(getCategoryEntries(searchState)) as CategoryEntryLite[])
                .filter((entry) => entry.facetId === 1)
                .pop();

            if (category?.name && category?.categoryId) {
                redirect?.(`/c/${urlSafeTitle(category.name)}/${category.categoryId}${preservedParams}`, true);
                return;
            }
        }

        if (combinationIds) {
            followUpPromises.push(dispatch(fetchCombinationCategory(combinationIds)));
        }

        if (itemIds?.length) {
            if (params?.deleted && !Number.isNaN(params.deleted)) {
                followUpPromises.push(
                    dispatch(
                        fetchItemSummariesIfNeeded({
                            identifier: 'MY-SAVED-SEARCH-PAGE-DELETED',
                            itemIds: [params.deleted],
                        })
                    )
                );
            }

            await Promise.all(followUpPromises);
            return;
        }

        const { soldItemIds = [] } = getSearchUiState(searchState);
        followUpPromises.push(
            dispatch(
                fetchItemSummariesIfNeeded({
                    identifier: 'MY-SAVED-SEARCH-PAGE-SOLD',
                    itemIds: soldItemIds,
                })
            )
        );
        followUpPromises.push(dispatch(fetchItemFacetsIfNeeded([soldItemIds[0]])));

        await Promise.all(followUpPromises);

        const stateAfterSold = getState();
        const soldItemId = soldItemIds[0];
        const sellerId = getItemSummarySellerId(stateAfterSold, soldItemId);
        const title = getItemSummaryTitle(stateAfterSold, soldItemId);
        const facets = getItemFacets(stateAfterSold, soldItemId);
        const categoryIds = getFacetIds(facets);

        await Promise.all([
            dispatch(
                fetchSimilarItemsIfNeeded({
                    categoryIds,
                    itemId: soldItemId,
                    sellerId,
                    title,
                })
            ),
        ]);

        const stateAfterSimilar = getState();
        const similarItemIds = getSimilarItemIds(stateAfterSimilar, soldItemId);
        await dispatch(
            fetchItemSummariesIfNeeded({
                identifier: 'MY-SAVED-SEARCH-PAGE-SIMILAR',
                itemIds: similarItemIds,
            })
        );
    });
};
